import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import "./main.scss";
import { agmsClientRequestGet } from "../../apis/request";
import AdminLayout from "./AdminLayout";
import secureLocalStorage from "react-secure-storage";
import XConv from "../../utils/confirmatoryExcel/core";
import XWrite from "../../utils/confirmatoryExcel/writer";

// 확증임상 엑셀 변환 — 날짜 파일(탭 1019 등)의 YSI·SMBG 를 대상자별 탭 엑셀(최종본)에 시간 순서대로 넣고,
// AGMS 칸은 서버 저장 혈당(user_glucose, 웹 그래프와 같은 값)으로 채워 '<이름>_변환.xlsx' 로 내려받는다.
// 엑셀은 브라우저 안에서만 처리(업로드 X). 차트·서식은 그대로, 차트 범위만 새 줄까지 늘린다.
// 열람: 최고 관리자(1001)만 (AdminLayout 메뉴 superOnly + 이 화면 + 서버 /api/admin/** 게이트).
export const canViewConfirmatoryExcel = () => Number(secureLocalStorage.getItem("role")) === 1001;

const DATE_TAB = /^\s*\d{4}\s*$/;
const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// ExcelJS·JSZip 은 이 화면에서만 쓰여서 번들에 넣지 않고 public/vendor 에서 처음 열 때 한 번 불러온다
const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
  const s = document.createElement("script");
  s.src = src; s.onload = resolve; s.onerror = () => reject(new Error(src + " 를 불러오지 못했습니다."));
  document.body.appendChild(s);
});
const loadLibs = async () => {
  const base = process.env.PUBLIC_URL || "";
  if (!window.ExcelJS) await loadScript(`${base}/vendor/exceljs-4.4.0.min.js`);
  if (!window.JSZip) await loadScript(`${base}/vendor/jszip-3.10.1.min.js`);
};
const loadBook = async (file) => { const wb = new window.ExcelJS.Workbook(); await wb.xlsx.load(await file.arrayBuffer()); return wb; };

// 스타일은 관리자 콘솔 다른 화면(기기 목록 등)과 같게
const thBase = {
  backgroundColor: "#787397", color: "#fff", fontWeight: "bold", fontFamily: "Noto Sans CJK KR",
  padding: "10px 16px", borderRight: "1px solid #EBEBEB", textAlign: "center",
};
const tdBase = {
  backgroundColor: "#fff", textAlign: "center", borderRight: "1px solid #EBEBEB",
  borderBottom: "1px solid #EBEBEB", padding: "8px 12px",
};
const primaryBtn = (disabled) => ({
  padding: "8px 16px", background: "#787397", color: "#fff", border: "none", borderRadius: "8px",
  fontWeight: "bold", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
});
const fileBox = {
  flex: "0 0 420px", maxWidth: "100%", padding: "8px 12px", border: "1px solid #CBCBCB", borderRadius: "8px",
  color: "#84818A", background: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const label = { width: "180px", flexShrink: 0, fontWeight: "bold" };

const AdminConfirmatoryExcel = () => {
  const allowed = canViewConfirmatoryExcel();
  const [target, setTarget] = useState(null);      // { file, sensors }
  const [dayFiles, setDayFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const pickTarget = async (file) => {
    setError(null); setResult(null);
    if (!file) return;
    try {
      await loadLibs();
      const wb = await loadBook(file);
      const sensors = wb.worksheets.map((ws) => XConv.normSensor(ws.name)).filter(Boolean);
      if (!sensors.length) {
        setTarget(null);
        setError(`${file.name} 에는 대상자별 탭(예: 2608096_L(5번 대상자))이 없습니다. 이 칸엔 확증임상 DATA(대상자별 탭 엑셀)를, 날짜별 파일(예: 10.19 혈당 측정값)은 '확증임상_혈당 측정값' 칸에 넣어 주세요.`);
        return;
      }
      setTarget({ file, sensors });
    } catch (e) { setTarget(null); setError("엑셀을 읽지 못했습니다: " + e.message); }
  };

  // 날짜 파일의 (센서, 날짜) → 서버 저장 혈당 {센서: {'MM-DD HH:MM': 값}}
  const fetchAgms = async (days) => {
    const recs = [];
    days.forEach((wb) => wb.worksheets.forEach((ws) => { if (DATE_TAB.test(ws.name)) recs.push(...XConv.readDateSheet(ws)); }));
    if (!recs.length) return { agms: {}, need: 0, dates: [] };
    const year = new Date().getFullYear();
    const sensors = Array.from(new Set(recs.map((r) => r.sensor)));
    const serials = Array.from(new Set(sensors.map((s) => s.split("_")[0])));
    const dates = Array.from(new Set(recs.map((r) => `${year}-${r.mmdd.slice(0, 2)}-${r.mmdd.slice(2)}`)));
    const res = await agmsClientRequestGet(`/api/admin/confirmatory/agms?serials=${serials.join(",")}&dates=${dates.join(",")}`);
    if (!res || typeof res !== "object" || res.error) throw new Error((res && res.error) || "AGMS 값을 불러오지 못했습니다(권한 또는 서버 오류).");
    const agms = {};
    sensors.forEach((s) => { agms[s] = res[s.split("_")[0]] || {}; });
    return { agms, need: recs.length, dates };
  };

  const run = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      await loadLibs();
      const targetBook = await loadBook(target.file);
      const days = [];
      for (const f of dayFiles) days.push(await loadBook(f));
      const { agms, dates } = await fetchAgms(days);
      const res = XConv.convert(targetBook, days, agms);
      const buf = await XWrite.write(await target.file.arrayBuffer(), res.plan,
        { JSZip: window.JSZip, DOMParser, XMLSerializer, outType: "uint8array" });
      const name = target.file.name.replace(/\.xlsx$/i, "") + "_변환.xlsx";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([buf], { type: XLSX_TYPE }));
      a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
      setResult({ ...res, name, dates });
    } catch (e) {
      setError("변환하다 문제가 생겼습니다: " + e.message);
    } finally { setBusy(false); }
  };

  if (!allowed) {
    return (
      <AdminLayout>
        <div style={{ padding: "40px", color: "#888" }}>접근 권한이 없습니다.</div>
      </AdminLayout>
    );
  }

  const added = result ? result.report.filter((r) => r.added) : [];
  const missingBy = (tab) => (result ? result.missingAgms.filter((m) => tab.startsWith(m.split(" ")[0])).length : 0);
  return (
    <AdminLayout>
      <div style={{ width: "100%" }}>
        <div style={{ marginBottom: "17px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: "bold", fontSize: "20px" }}>확증임상 엑셀 변환</div>
          <div style={{ color: "#84818A" }}>엑셀은 브라우저 안에서만 처리돼요</div>
        </div>
        <div style={{ color: "#84818A", marginBottom: "14px" }}>
          확증임상_혈당 측정값의 YSI·SMBG 를 확증임상 DATA 의 대상자별 탭에 시간 순서대로 넣고, AGMS 칸은 서버 저장 혈당(웹 그래프와 같은 값)으로 채워 '이름_변환.xlsx' 로 내려받습니다.
        </div>

        {error && <div style={{ color: "#d9534f", marginBottom: "12px" }}>{error}</div>}

        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <div style={label}>확증임상 DATA</div>
          <div style={fileBox}>{target ? `${target.file.name} · 대상자 탭 ${target.sensors.length}개` : "예: 최종본.xlsx"}</div>
          <label style={{ ...primaryBtn(false), margin: 0 }}>
            파일 선택
            <input type="file" accept=".xlsx" style={{ display: "none" }} onChange={(e) => { pickTarget(e.target.files[0]); e.target.value = ""; }} />
          </label>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
          <div style={label}>확증임상_혈당 측정값</div>
          <div style={fileBox}>{dayFiles.length ? dayFiles.map((f) => f.name).join(", ") : "탭 이름이 4자리 날짜(예: 1019) · 여러 개 가능"}</div>
          <label style={{ ...primaryBtn(false), margin: 0 }}>
            파일 선택
            <input type="file" accept=".xlsx" multiple style={{ display: "none" }} onChange={(e) => { setDayFiles(Array.from(e.target.files)); setResult(null); e.target.value = ""; }} />
          </label>
        </div>

        <button style={primaryBtn(!target || busy)} disabled={!target || busy} onClick={run}>
          {busy ? "변환 중…" : "변환해서 내려받기"}
        </button>

        {result && (
          <div style={{ marginTop: "24px" }}>
            <div style={{ marginBottom: "10px", color: "#84818A" }}>
              읽은 날짜 탭: {result.readTabs.join(", ") || "없음"} · 저장: {result.name} (다운로드 폴더)
            </div>
            {result.unknown.length > 0 && (
              <div style={{ color: "#d9534f", marginBottom: "10px" }}>탭이 없는 대상자(시리얼 오타 확인): {result.unknown.join(", ")}</div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table style={{ minWidth: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ ...thBase, borderRadius: "5px 0px 0px 5px" }}>대상자 탭</th>
                    <th style={thBase}>추가한 줄</th>
                    <th style={{ ...thBase, borderRight: "none", borderRadius: "0px 5px 5px 0px" }}>AGMS 빈 칸</th>
                  </tr>
                </thead>
                <tbody>
                  {added.length === 0 ? (
                    <tr><td style={{ ...tdBase, borderRight: "none" }} colSpan={3}>추가할 새 줄이 없습니다(이미 다 들어 있음).</td></tr>
                  ) : added.map((r) => (
                    <tr key={r.tab}>
                      <td style={tdBase}>{r.tab}</td>
                      <td style={tdBase}>{r.added}</td>
                      <td style={{ ...tdBase, borderRight: "none", color: missingBy(r.tab) ? "#d9534f" : undefined }}>{missingBy(r.tab)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default withRouter(AdminConfirmatoryExcel);
